/**
 * Frontend interactivity for taxonomy filter block
 * Properly integrates with WordPress Interactivity API
 */

import { store, getContext, getServerState } from '@wordpress/interactivity';

/**
 * Helper function to update URL
 */
const updateURL = async (taxonomy, values) => {
	const url = new URL(window.location);
	const paramName = `sdlg_filter_${taxonomy}`;

	// Remove existing params
	const keysToDelete = [];
	for (const key of url.searchParams.keys()) {
		if (key === paramName || key === `${paramName}[]`) {
			keysToDelete.push(key);
		}
	}
	keysToDelete.forEach(key => url.searchParams.delete(key));

	// Add new values
	if (values.length > 0) {
		values.forEach(value => {
			url.searchParams.append(`${paramName}[]`, value);
		});
	}
	// NOTE: this works but product-filters overwrite it
	console.log('updateURL about to navigate', `${window.location.pathname}${url.search}`);
	const { actions } = await import('@wordpress/interactivity-router');
	console.log('actions', actions);
	await actions.navigate(`${window.location.pathname}${url.search}`);
	// return url;
};

/**
 * Auto-expand parent items that have selected children
 */
const autoExpandActiveItems = (context) => {
	if (!context.selectedTerms || context.selectedTerms.length === 0) {
		return;
	}

	// Find the block element
	const element = document.querySelector(`[data-block-id="${context.blockId}"]`);
	if (!element) {
		return;
	}

	// Find all checked checkboxes
	const checkboxes = element.querySelectorAll('.sd-filter-taxonomy__checkbox:checked');
	const expandedTermIds = [];

	checkboxes.forEach(checkbox => {
		let parent = checkbox.closest('.sd-filter-taxonomy__item');

		// Walk up the tree and collect all parent term IDs
		while (parent) {
			if (parent.classList.contains('has-children')) {
				const parentCheckbox = parent.querySelector(':scope > .sd-filter-taxonomy__item-wrapper .sd-filter-taxonomy__checkbox');
				if (parentCheckbox) {
					// Store as string to match the directive comparison
					const parentTermId = String(parentCheckbox.dataset.termId);
					if (!expandedTermIds.includes(parentTermId)) {
						expandedTermIds.push(parentTermId);
					}
				}
			}

			// Move to next parent
			const parentList = parent.closest('.sd-filter-taxonomy__children');
			if (parentList) {
				parent = parentList.closest('.sd-filter-taxonomy__item');
			} else {
				parent = null;
			}
		}
	});

	context.expandedItems = expandedTermIds;
	console.log('Auto-expanded items:', expandedTermIds);
}

/**
 * Check scroll indicator
 */
const checkScrollIndicator = (element) => {
	const body = element.querySelector('.sd-filter-taxonomy__body');
	if (!body) {
		return;
	}

	const isScrollable = body.scrollHeight > body.clientHeight;

	if (isScrollable) {
		body.classList.add('has-scroll');

		const scrollHandler = () => {
			const isAtBottom = body.scrollHeight - body.scrollTop <= body.clientHeight + 5;
			if (isAtBottom) {
				body.classList.remove('has-scroll');
			} else {
				body.classList.add('has-scroll');
			}
		};

		body.removeEventListener('scroll', scrollHandler);
		body.addEventListener('scroll', scrollHandler);
	} else {
		body.classList.remove('has-scroll');
	}
}

/**
 * Setup resize observer for scroll indicators
 */
const setupScrollObserver = () => {
	const filterBlocks = document.querySelectorAll('.sd-filter-taxonomy');

	filterBlocks.forEach(block => {
		const body = block.querySelector('.sd-filter-taxonomy__body');
		if (body) {
			const resizeObserver = new ResizeObserver(() => {
				checkScrollIndicator(block);
			});
			resizeObserver.observe(body);
		}
	});
}

// Define the store for taxonomy filters
const { state } = store('sdlg/taxonomy-filter', {
	state: {
		get selectedTerms() {
			const context = getContext();
			return context.selectedTerms || [];
		},
		get isExpanded() {
			const context = getContext();
			return context.isExpanded || false;
		},
		get hasSelectedTerms() {
			const context = getContext();
			return context.selectedTerms && context.selectedTerms.length > 0;
		},
		get isItemExpanded() {
			const context = getContext();
			if (!context.expandedItems || !context.itemTermId) {
				return false;
			}
			return context.expandedItems.includes(String(context.itemTermId));
		},
		get isTermSelected() {
			const context = getContext();
			if (!context.selectedTerms || !context.termSlug) {
				return false;
			}
			return context.selectedTerms.includes(context.termSlug);
		}
	},
	actions: {
		toggleTerm: (event) => {
			const context = getContext();
			const checkbox = event.target;
			const value = checkbox.value;

			// Initialize selectedTerms if not present
			if (!context.selectedTerms) {
				context.selectedTerms = [];
			}

			// Update selected terms
			if (checkbox.checked) {
				if (!context.selectedTerms.includes(value)) {
					context.selectedTerms = [...context.selectedTerms, value];
				}
			} else {
				context.selectedTerms = context.selectedTerms.filter(v => v !== value);
			}

			// TODO: this should do something different if not interactivity api enabled
			// Update URL
			updateURL(context.taxonomy, context.selectedTerms);
		},

		clearFilters: () => {
			const context = getContext();
			context.selectedTerms = [];

			// Update URL
			updateURL(context.taxonomy, []);

			// Trigger WooCommerce filter update
			if (context.useInteractivity) {
				// triggerWooCommerceUpdate(context);
			}

			// Dispatch event
			document.dispatchEvent(new CustomEvent('sd-filters-changed', {
				detail: {
					taxonomy: context.taxonomy,
					values: []
				}
			}));
		},

		toggleAccordion: () => {
			const context = getContext();
			context.isExpanded = !context.isExpanded;

			// Check scroll indicator after animation
			const element = document.querySelector(`[data-block-id="${context.blockId}"]`);
			if (element && context.isExpanded) {
				setTimeout(() => {
					checkScrollIndicator(element);
				}, 350);
			}
		},

		toggleItem: (event) => {
			const context = getContext();

			// Get the button element - could be the button or svg/path inside it
			let button = event.target;
			if (button.tagName !== 'BUTTON') {
				button = event.target.closest('button');
			}

			if (!button) {
				console.warn('Toggle button not found');
				return;
			}

			// Get term ID as string to match how it's checked in the directive
			const termId = String(button.dataset.termId);

			if (!termId || termId === 'undefined') {
				console.warn('Term ID not found on button', button);
				return;
			}

			if (!context.expandedItems) {
				context.expandedItems = [];
			}

			// Toggle the term ID in the expanded items array (as strings)
			if (context.expandedItems.includes(termId)) {
				context.expandedItems = context.expandedItems.filter(id => id !== termId);
			} else {
				context.expandedItems = [...context.expandedItems, termId];
			}

			console.log('Toggled term:', termId, 'Expanded items:', context.expandedItems);

			// Check scroll indicator after animation
			const element = document.querySelector(`[data-block-id="${context.blockId}"]`);
			if (element) {
				setTimeout(() => {
					checkScrollIndicator(element);
				}, 350);
			}
		}
	},
	callbacks: {
		onInit: () => {
			const context = getContext();

			// Initialize from URL
			const url = new URL(window.location);
			const paramName = `sdlg_filter_${context.taxonomy}`;
			let urlValues = url.searchParams.getAll(`${paramName}[]`);

			if (urlValues.length === 0) {
				const singleValue = url.searchParams.get(paramName);
				if (singleValue) {
					urlValues = [singleValue];
				}
			}

			context.selectedTerms = urlValues;
			context.expandedItems = [];

			// Auto-expand items with selected children
			autoExpandActiveItems(context);

			// Check scroll indicator after init
			const element = document.querySelector(`[data-block-id="${context.blockId}"]`);
			if (element && context.isExpanded) {
				setTimeout(() => {
					checkScrollIndicator(element);
				}, 100);
			}
		},
	}
});

// Initialize scroll observers after DOM is ready
document.addEventListener('DOMContentLoaded', () => {
	setupScrollObserver();
	// console.log('SD Filter Taxonomy with Interactivity API initialized');
	// console.log('store', store);
	// const productFilterContext = getContext('woocommerce/product-filter')
	// console.log('productFilterContext', productFilterContext);
	// const productFilterStore = store('woocommerce/product-filter')
	// console.log('productFilterStore', productFilterStore);
});

// Handle browser back/forward buttons
window.addEventListener('popstate', () => {
	// The Interactivity API will handle re-initialization
	// But we need to trigger scroll checks
	setTimeout(() => {
		setupScrollObserver();
	}, 100);
});
